import { buildApiUrl, ADMIN_ENDPOINTS } from '../../configs/api.js'

export default {
  url(payload) {
    const {
      status,
      userId,
      riderId,
      startDate,
      endDate,
    } = payload || {}

    return buildApiUrl(ADMIN_ENDPOINTS.ADMIN_PACKAGES, {
      queryParams: {
        limit: 2000,
        status,
        userId,
        riderId,
        startDate,
        endDate,
      },
    })
  },

  parse(raw = {}, payload = {}) {
    if (raw.statusCode !== 200) {
      return {
        statusCode: raw?.statusCode || 400,
        message: raw?.message || 'Failed to fetch packages',
        data: null,
      }
    }

    const packagesArray = Array.isArray(raw.data?.data)
      ? raw.data.data
      : raw.data?.data?.packages || []

    const pagination = raw.data || {}

    // 1. Mapping complet des colis
    const formattedPackages = packagesArray.map(pkg => {
      // Extractions Pickup & Destination
      const pickup = pkg.pickup || {}
      const destination = pkg.destination || {}
      const packageInfo = pkg.packageInfo || {}
      const pricing = pkg.pricing || pkg.order?.pricing || {}
      const payment = pkg.payment || pkg.order?.payment || {}

      // Informations Expéditeur
      const customerName =
        pkg.customer?.name ||
        pickup.contactName ||
        pkg.senderName ||
        pkg.userName ||
        'Inconnu'
      const customerPhoneNumber =
        pkg.customer?.phone ||
        pickup.phone ||
        pkg.senderPhone ||
        pkg.userPhone ||
        ''
      const customerEmail = pkg.customer?.email || pkg.userEmail || ''
      const customerAvatar = pkg.customer?.avatar || pkg.customerAvatar || null

      // Informations Destinataire
      const recipientName =
        pkg.recipient?.name ||
        destination.contactName ||
        pkg.recipientName ||
        'Inconnu'
      const recipientPhoneNumber =
        pkg.recipient?.phone ||
        destination.phone ||
        pkg.recipientPhoneNumber ||
        ''
      const recipientEmail = pkg.recipient?.email || ''

      // Informations Livreur
      const riderInfo = pkg.rider || pkg.delivery?.rider || pkg.assignedRider || {}
      const riderId = pkg.riderId || pkg.delivery?.riderId || riderInfo.id || riderInfo._id || null
      const riderName = pkg.riderName || riderInfo.name || riderInfo.fullName || (riderInfo.firstName ? `${riderInfo.firstName} ${riderInfo.lastName || ''}`.trim() : '') || null
      const riderPhoneNumber = pkg.riderPhoneNumber || riderInfo.phone || riderInfo.phoneNumber || ''
      const riderVehicle =
        pkg.riderVehicle ||
        riderInfo.vehicle ||
        riderInfo.vehicleType ||
        packageInfo.pickupMethod ||
        pkg.deliveryType ||
        '-'
      const riderAvatar = pkg.riderAvatar || riderInfo.avatar || riderInfo.photoUrl || null

      // Distances & Durée
      const distanceKm = pkg.distanceKm ?? pkg.route?.totalDistance ?? 0
      const durationMin = pkg.durationMin ?? pkg.route?.totalDuration ?? 0

      // Tarification
      const deliveryFee = pricing.deliveryFee || 0
      const serviceFee = pricing.serviceFee || pricing.paymentFee || 0
      const totalPrice = pricing.total || pkg.price || deliveryFee + serviceFee
      const currency = pricing.currency || pkg.currency || 'XOF'

      // Type & Véhicule
      const packageType = packageInfo.category || pkg.packageType || 'parcel'
      const deliveryType =
        packageInfo.pickupMethod || pkg.deliveryType || 'moto'

      const stepsTimeline = {
        reference: pkg.createdAt,
        created: pkg.createdAt,
        payment:
          payment.paidAt ||
          (payment.status === 'paid' ? pkg.createdAt : null),
        confirmed: pkg.acceptedAt || pkg.confirmedAt || pkg.assignedAt,
        assigned: pkg.assignedAt || pkg.acceptedAt,
        pickedUp: pkg.pickedUpAt || pkg.picked_up_at,
        picked_up: pkg.pickedUpAt || pkg.picked_up_at,
        delivered: pkg.deliveredAt || pkg.delivered_at,
        cancelled: pkg.cancelledAt || pkg.cancelled_at,
      }

      if (Array.isArray(pkg.timeline)) {
        pkg.timeline.forEach(event => {
          if (event.status && event.timestamp) {
            stepsTimeline[event.status] = event.timestamp
          }
        })
      }

      return {
        // Core fields
        id: String(pkg.id || pkg._id || ''),
        reference: String(pkg.reference || pkg.orderReference || ''),
        title: String(
          pkg.title || pkg.packageInfo?.description || 'Livraison de colis'
        ),
        description: String(packageInfo.description || pkg.description || ''),

        // Customer / Expéditeur
        userId: pkg.userId,
        customerId: pkg.customer?.id || pkg.userId,
        customerName: String(customerName),
        customerPhoneNumber: String(customerPhoneNumber),
        customerAvatar,
        customerEmail: String(customerEmail),

        // Recipient / Destinataire
        recipientName: String(recipientName),
        recipientPhoneNumber: String(recipientPhoneNumber),
        recipientEmail: String(recipientEmail),

        // Rider / Livreur
        riderId: riderId ? String(riderId) : null,
        riderName: String(riderName || ''),
        riderPhoneNumber: String(riderPhoneNumber),
        riderVehicle,
        riderAvatar,
        rider: {
          id: riderId ? String(riderId) : null,
          name: riderName || '',
          phone: riderPhoneNumber,
          vehicle: riderVehicle,
          avatar: riderAvatar,
          lat: pkg.currentLocation?.latitude || pkg.rider?.lat || pkg.riderLat,
          lng: pkg.currentLocation?.longitude || pkg.rider?.lng || pkg.riderLng,
        },

        // Adresses & Coordonnées
        pickup: {
          address: String(pickup.address || ''),
          lat: pickup.latitude ?? pickup.lat,
          lng: pickup.longitude ?? pickup.lng,
          contactName: String(pickup.contactName || ''),
          phone: String(pickup.phone || ''),
        },
        destination: {
          address: String(destination.address || ''),
          lat: destination.latitude ?? destination.lat,
          lng: destination.longitude ?? destination.lng,
          contactName: String(destination.contactName || ''),
          phone: String(destination.phone || ''),
        },
        pickupAddress: String(pickup.address || pkg.pickupAddress || ''),
        pickupLabel: String(pickup.address || pkg.pickupLabel || ''),
        pickupLat: pickup.latitude ?? pkg.pickupLat,
        pickupLng: pickup.longitude ?? pkg.pickupLng,

        destinationAddress: String(destination.address || pkg.destinationAddress || ''),
        destinationLabel: String(destination.address || pkg.destinationLabel || ''),
        destinationLat: destination.latitude ?? pkg.destinationLat,
        destinationLng: destination.longitude ?? pkg.destinationLng,

        checkpoints: (pkg.checkpoints || []).map(cp => ({
          address: cp.address || cp.locationText || '',
          lat: cp.latitude ?? cp.lat,
          lng: cp.longitude ?? cp.lng,
          contactName: cp.contactName || cp.name || '',
          phone: cp.phone || cp.contactPhone || '',
        })),

        // Détails du Colis
        packageInfo: {
          category: packageType,
          quantity: packageInfo.quantity || 1,
          value: packageInfo.value || 0,
          weight: packageInfo.weight || { value: 0, unit: 'kg' },
          dimensions: packageInfo.dimensions || {
            length: 0,
            width: 0,
            height: 0,
            unit: 'cm',
          },
          fragile: packageInfo.fragile || false,
          pickupMethod: deliveryType,
          pickupScheduledAt: packageInfo.pickupScheduledAt || null,
          metadata: packageInfo.metadata || {},
        },
        packageType,
        deliveryType,

        // Statut & Dates
        status: String(pkg.status || ''),
        orderDate: pkg.createdAt,
        createdAt: pkg.createdAt,
        updatedAt: pkg.updatedAt,
        acceptedAt: pkg.acceptedAt || stepsTimeline.confirmed,
        pickedUpAt: pkg.pickedUpAt || stepsTimeline.picked_up,
        deliveredAt: pkg.deliveredAt || stepsTimeline.delivered,
        cancelledAt: pkg.cancelledAt || stepsTimeline.cancelled,

        // Tarification
        pricing: {
          deliveryFee,
          serviceFee,
          deliveryCost: deliveryFee,
          total: totalPrice,
          currency,
          discount: pkg.discount || { amount: 0 },
        },
        totalPrice,
        deliveryFee,
        serviceFee,

        // Paiement
        payment: {
          status: payment.status || 'pending',
          method: payment.method || 'cash',
          provider: payment.provider || payment.processor || 'cash',
          phoneNumber: payment.phoneNumber || '',
          transactionId: payment.transactionId || '',
          providerReference: payment.providerReference || '',
          paidAt: payment.paidAt || null,
        },

        // Suivi & Trajet
        distanceKm,
        durationMin,
        route: {
          totalDistance: distanceKm,
          totalDuration: durationMin,
          distanceFormatted: `${distanceKm} km`,
          durationFormatted: `${durationMin} min`,
          totalStops: pkg.route?.totalStops || 1,
          completedStops: pkg.route?.completedStops || 0,
        },

        // Timelines
        timeline: pkg.timeline || [],
        stepsTimeline: stepsTimeline,
        notes: pkg.notes || '',
      }
    })

    // 2. Application des filtres locaux
    let filtered = formattedPackages

    if (payload?.riderId) {
      const targetRiderId = String(payload.riderId)
      filtered = filtered.filter(p => p.riderId === targetRiderId || p.rider?.id === targetRiderId)
    }

    const searchTerm = (payload?.search || '').toString().trim().toLowerCase()
    if (searchTerm) {
      filtered = filtered.filter(p => {
        return (
          p.id.toLowerCase().includes(searchTerm) ||
          p.reference.toLowerCase().includes(searchTerm) ||
          p.customerName.toLowerCase().includes(searchTerm) ||
          p.customerPhoneNumber.toLowerCase().includes(searchTerm) ||
          p.customerEmail.toLowerCase().includes(searchTerm) ||
          p.recipientName.toLowerCase().includes(searchTerm) ||
          p.recipientPhoneNumber.toLowerCase().includes(searchTerm) ||
          p.riderName.toLowerCase().includes(searchTerm) ||
          p.riderPhoneNumber.toLowerCase().includes(searchTerm) ||
          p.pickupAddress.toLowerCase().includes(searchTerm) ||
          p.destinationAddress.toLowerCase().includes(searchTerm) ||
          p.title.toLowerCase().includes(searchTerm) ||
          p.description.toLowerCase().includes(searchTerm)
        )
      })
    }

    // === Tri dynamique selon payload.sortKey et sortOrder ===
    let finalData = filtered
    const { sortKey, sortOrder } = payload || {}
    if (sortKey) {
      const order = sortOrder === 'desc' ? -1 : 1
      finalData = [...finalData].sort((a, b) => {
        const valA = a?.[sortKey]
        const valB = b?.[sortKey]
        if (typeof valA === 'string' && typeof valB === 'string') return valA.localeCompare(valB) * order
        if (typeof valA === 'number' && typeof valB === 'number') return (valA - valB) * order
        return 0
      })
    }

    // === Pagination locale ===
    const { page = 0, limit = 20 } = payload || {}
    const paginated = finalData.slice(page * limit, (page + 1) * limit)

    // 3. Retour avec mise à jour du total
    return {
      statusCode: 200,
      message: 'Ok',
      data: {
        total: searchTerm ? filtered.length : (pagination.total || formattedPackages.length),
        result: paginated,
      },
    }
  },
}